import React, { useState } from 'react';
import moment from 'moment';
import { Users, CheckCircle, Clock, AlertCircle, X } from 'lucide-react';

const DetalleActividadGrupo = ({
    actividadGrupo,
    onClose,
    onCompletarTodos,
    onCompletarParticipante,
    esFechaVencida
}) => {
    const [expandedParticipants, setExpandedParticipants] = useState(false);

    const getEstadoClasses = (estado, fechaProgramada) => {
        if (estado === 'completada') {
            return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20';
        }
        if (esFechaVencida(fechaProgramada)) {
            return 'bg-rose-50 text-rose-700 ring-1 ring-rose-600/20';
        }
        return 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20';
    };

    const getEstadoText = (estado, fechaProgramada) => {
        if (estado === 'completada') return 'Completada';
        if (esFechaVencida(fechaProgramada)) return 'Vencida';
        return 'Pendiente';
    };

    const getEstadoGeneralClasses = () => {
        if (actividadGrupo.estado_general === 'completada') {
            return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20';
        }
        if (actividadGrupo.estado_general === 'parcial') {
            return 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20';
        }
        if (esFechaVencida(actividadGrupo.fecha_programada)) {
            return 'bg-rose-50 text-rose-700 ring-1 ring-rose-600/20';
        }
        return 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20';
    };

    const getEstadoGeneralText = () => {
        if (actividadGrupo.estado_general === 'completada') return 'Completada';
        if (actividadGrupo.estado_general === 'parcial') return 'Parcialmente completada';
        if (esFechaVencida(actividadGrupo.fecha_programada)) return 'Vencida';
        return 'Pendiente';
    };

    const completadas = actividadGrupo.participantes.filter(p => p.estado === 'completada').length;
    const total = actividadGrupo.participantes.length;
    const progreso = (completadas / total) * 100;

    const participantesAMostrar = expandedParticipants 
        ? actividadGrupo.participantes 
        : actividadGrupo.participantes.slice(0, 5);

    return (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-[600px] max-w-full mx-4 shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-start">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <h2 className="text-lg font-semibold text-slate-900">
                                {actividadGrupo.nombre}
                            </h2>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex items-center gap-1 ${getEstadoGeneralClasses()}`}>
                                {actividadGrupo.estado_general === 'completada' && <CheckCircle className="w-3 h-3" />}
                                {actividadGrupo.estado_general === 'parcial' && <Clock className="w-3 h-3" />}
                                {actividadGrupo.estado_general === 'pendiente' && <Clock className="w-3 h-3" />}
                                {getEstadoGeneralText()}
                            </span>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                            <div className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                <span>{total} participante{total !== 1 ? 's' : ''}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span>{moment(actividadGrupo.fecha_programada).format('DD/MM/YYYY')}</span>
                            </div>
                        </div>
                    </div>
                    
                    <button 
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                {/* Contenido scrolleable */}
                <div className="flex-1 overflow-y-auto">
                    <div className="p-6 space-y-6">
                        {/* Progreso general */}
                        <div className="bg-slate-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-slate-700">Progreso general</span>
                                <span className="text-sm font-semibold text-slate-900">
                                    {completadas}/{total} completadas
                                </span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                                <div 
                                    className={`h-2 rounded-full transition-all duration-300 ${
                                        progreso === 100 ? 'bg-emerald-500' : 
                                        progreso > 0 ? 'bg-amber-500' : 'bg-slate-300'
                                    }`}
                                    style={{ width: `${progreso}%` }}
                                ></div>
                            </div>
                        </div>

                        {/* Descripción */}
                        {actividadGrupo.descripcion && (
                            <div>
                                <p className="text-sm font-medium text-slate-500 mb-2">Descripción</p>
                                <p className="text-sm text-slate-700 leading-relaxed">
                                    {actividadGrupo.descripcion}
                                </p>
                            </div>
                        )}

                        {/* Lista de participantes */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-medium text-slate-500">Participantes</p>
                                {actividadGrupo.estado_general !== 'completada' && (
                                    <button
                                        onClick={() => onCompletarTodos(actividadGrupo)}
                                        className="px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                                    >
                                        Completar todos
                                    </button>
                                )}
                            </div>
                            
                            <div className="space-y-3">
                                {participantesAMostrar.map((participante) => (
                                    <div key={participante.id_actividad} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-3 h-3 rounded-full ${
                                                participante.estado === 'completada' ? 'bg-emerald-500' : 
                                                esFechaVencida(participante.fecha_programada) ? 'bg-rose-500' : 'bg-slate-300'
                                            }`}></div>
                                            <div>
                                                <p className="text-sm font-medium text-slate-900">
                                                    {participante.detallesNino.nombre}
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getEstadoClasses(participante.estado, participante.fecha_programada)}`}>
                                                        {getEstadoText(participante.estado, participante.fecha_programada)}
                                                    </span>
                                                    {participante.observaciones && (
                                                        <span className="text-xs text-slate-500">Con observaciones</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {participante.estado !== 'completada' && (
                                            <button
                                                onClick={() => onCompletarParticipante(actividadGrupo, participante)}
                                                className="px-3 py-1.5 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors font-medium"
                                            >
                                                Completar
                                            </button>
                                        )}
                                    </div>
                                ))}
                                
                                {actividadGrupo.participantes.length > 5 && (
                                    <button
                                        onClick={() => setExpandedParticipants(!expandedParticipants)}
                                        className="w-full p-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-colors"
                                    >
                                        {expandedParticipants 
                                            ? 'Ver menos participantes' 
                                            : `Ver ${actividadGrupo.participantes.length - 5} participantes más`
                                        }
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Observaciones de participantes completados */}
                        {actividadGrupo.participantes.some(p => p.estado === 'completada' && p.observaciones) && (
                            <div>
                                <p className="text-sm font-medium text-slate-500 mb-3">Observaciones</p>
                                <div className="space-y-2">
                                    {actividadGrupo.participantes
                                        .filter(p => p.estado === 'completada' && p.observaciones)
                                        .map((participante) => (
                                            <div key={participante.id_actividad} className="p-3 bg-emerald-50 rounded-lg">
                                                <p className="text-sm font-medium text-emerald-800 mb-1">
                                                    {participante.detallesNino.nombre}
                                                </p>
                                                <p className="text-sm text-emerald-700">
                                                    {participante.observaciones}
                                                </p>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DetalleActividadGrupo;